import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, Lock, LogOut } from "lucide-react";
import useAuth from "@/lib/useAuth";
import ElpmLogo from "@/components/ElpmLogo";

function GateFrame({ testId, children }) {
  return (
    <div data-testid={testId} className="creator-door relative grid min-h-screen place-items-center overflow-hidden px-6 text-white">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -left-40 top-[-120px] h-[520px] w-[520px] rounded-full bg-blue-600/20 blur-[140px]" />
        <div className="absolute right-[-160px] top-[30%] h-[560px] w-[560px] rounded-full bg-violet-600/20 blur-[160px]" />
      </div>
      <div className="w-full max-w-md rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/85 p-8 text-center shadow-[0_30px_80px_rgba(0,0,0,0.5)] backdrop-blur-xl">
        <ElpmLogo className="mx-auto mb-6 h-14" testId="studio-gate-logo" />
        {children}
      </div>
    </div>
  );
}

/** Private studio: only the owner's Google account (OWNER_EMAILS / co-owner flag) gets past this. */
export default function StudioGate({ children }) {
  const { user, loading, signIn, signOut } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Library rows are scoped to the signed-in user id — keep this browser's token on it.
    if (!user?.is_owner) return;
    try {
      if (localStorage.getItem("elpm-user-token") !== user.user_id) localStorage.setItem("elpm-user-token", user.user_id);
    } catch (e) { void e; }
  }, [user]);

  if (loading) {
    return (
      <GateFrame testId="studio-gate-loading">
        <p className="flex items-center justify-center gap-2 text-white/70"><Loader2 className="h-4 w-4 animate-spin" /> Opening your studio…</p>
      </GateFrame>
    );
  }
  if (!user) {
    return (
      <GateFrame testId="studio-gate-signin">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-300/80">ELPM Studio</p>
        <h1 className="mt-3 font-display text-3xl font-bold">Private studio</h1>
        <p className="mt-3 text-sm leading-relaxed text-white/65">Sign in with the studio owner's Google account to open your music, voices and projects.</p>
        <button
          type="button"
          data-testid="studio-gate-signin-btn"
          onClick={() => signIn(location.pathname + location.search)}
          className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-6 py-3.5 font-display font-semibold shadow-[0_14px_44px_rgba(99,102,241,0.4)] transition-transform duration-200 hover:scale-[1.02]"
        >
          <Lock className="h-4 w-4" /> Sign in with Google
        </button>
      </GateFrame>
    );
  }
  if (!user.is_owner) {
    return (
      <GateFrame testId="studio-gate-restricted">
        <h1 className="font-display text-2xl font-bold">This studio is private</h1>
        <p className="mt-3 text-sm leading-relaxed text-white/65">
          You're signed in as <span data-testid="studio-gate-email" className="text-white">{user.email}</span>, which doesn't have access to this studio.
        </p>
        <button
          type="button"
          data-testid="studio-gate-signout-btn"
          onClick={signOut}
          className="mt-8 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-6 py-3 font-display text-sm font-semibold transition-colors hover:bg-white/[0.12]"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </GateFrame>
    );
  }
  return children;
}
